// Suggestions under a text field: a list that opens when the field is focused, narrows as it is typed into, and fills
// the field with the one picked (a click, or the arrow keys and Return; Escape closes it). Drawn as a popover, in the
// top layer, so it shows over a modal dialog and is never clipped by one. Used by the shortcut dialog's Opens field
// (client/lib/shortcuts.js): the applets' commands and the recent webtops.
const MAX = 50;

/**
 * Gives `input` a suggestion list. `source(text)` answers what to offer for what is typed, `[{value, label?, ...}]`,
 * asked each time the list is drawn; an entry is shown when its value or label contains the text. Picking one sets the
 * field's value, fires `input` and `change` on it, as typing would, calls `onPick(entry)` (the whole entry, with any
 * fields of the caller's own), then draws the list again for the new value: it stays open when the source has more
 * to offer from there (an applet's commands once the applet is picked).
 */
export function suggest(input, source, onPick = null) {
  const list = document.createElement('ul');
  list.className = 'suggest';
  list.popover = 'manual';
  list.setAttribute('role', 'listbox');
  input.setAttribute('aria-autocomplete', 'list');
  input.after(list);
  let items = [];
  let at = -1;
  let picking = false;
  const shown = () => list.matches(':popover-open');
  const hide = () => { if (shown()) list.hidePopover(); at = -1; };
  function place() {
    const r = input.getBoundingClientRect();
    Object.assign(list.style, { left: `${r.left}px`, top: `${r.bottom + 4}px`, width: `${r.width}px` });
  }
  function draw() {
    if (picking) return;
    const q = input.value.trim().toLowerCase();
    items = source(input.value).filter((it) => !q || `${it.value}\n${it.label ?? ''}`.toLowerCase().includes(q)).slice(0, MAX);
    // nothing to offer, or only what is already there
    if (!items.length || (items.length === 1 && items[0].value === input.value.trim())) return hide();
    at = -1;
    list.replaceChildren(...items.map((it, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.append(Object.assign(document.createElement('span'), { className: 'value', textContent: it.value }));
      if (it.label) li.append(Object.assign(document.createElement('span'), { className: 'label', textContent: it.label }));
      // pointerdown, not click: the field keeps the focus, so the list is not closed by its blur first
      li.addEventListener('pointerdown', (e) => { e.preventDefault(); pick(i); });
      return li;
    }));
    place();
    if (!shown()) list.showPopover();
  }
  function pick(i) {
    const entry = items[i];
    input.value = entry.value;
    hide();
    picking = true;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    picking = false;
    onPick?.(entry);
    draw();
  }
  function mark(i) {
    at = (i + items.length) % items.length;
    [...list.children].forEach((li, n) => li.setAttribute('aria-selected', String(n === at)));
    list.children[at]?.scrollIntoView({ block: 'nearest' });
  }
  input.addEventListener('focus', draw);
  input.addEventListener('input', draw);
  input.addEventListener('blur', hide);
  input.addEventListener('keydown', (e) => {
    if (e.isComposing) return;
    if (!shown()) {
      if (e.key === 'ArrowDown') { e.preventDefault(); draw(); }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); mark(at + (e.key === 'ArrowDown' ? 1 : -1)); }
    else if (e.key === 'Enter' && at >= 0) { e.preventDefault(); pick(at); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); hide(); } // the list closes, not the dialog
  });
  // a dialog that scrolls or a window that resizes moves the field: the list follows it
  addEventListener('scroll', () => { if (shown()) place(); }, true);
  addEventListener('resize', () => { if (shown()) place(); });
}
